import React, { useEffect, useState } from 'react'
import { Box, DialogContent } from '@mui/material'
import { Button, Dialog, IconButton, makeStyles, Paper, Typography } from '@material-ui/core'
import {
  Add,
  Close,
  DeleteOutlineOutlined as DeleteIcon,
  EditOutlined as EditIcon,
  Image as ImageIcon
} from '@material-ui/icons'
import { useHistory } from 'react-router-dom'
import badgesSlice, { deleteBadgeConfiguration, getBadgeConfigurations } from 'store/badges'
import { useDispatch, useSelector } from 'react-redux'
import { renderCol } from 'utils/renderColumns'
import Tooltip from '@material-ui/core/Tooltip'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import getTableOptions from 'utils/getTableOptions'
import ConfirmModal from 'components/ConfirmationModal'
import AvatarPicker from 'components/AvatarPicker'
import { Collection } from 'constants/index'
import { toast } from 'utils/notify'
import { updateConferenceById } from 'store/conference'

const ConfigurationListing = () => {
  const history = useHistory()
  const classes = useStyles()
  const dispatch = useDispatch()
  const { conference } = useSelector((state) => state.conference)
  const { badgeConfigurations, currentPage, pageSize } = useSelector((state) => state.badges)
  const [deletedConfigurationId, setDeletedConfigurationId] = useState('')
  const [deletingConfiguration, setDeletingConfiguration] = React.useState(false)
  const [openBadgeBannerModal, setOpenBadgeBannerModal] = useState(false)

  useEffect(() => {
    if (conference?.id) {
      const unsub = getBadgeConfigurations(conference?.id, (data) => {
        dispatch(badgesSlice.actions.setBadgeConfigurations(data))
      })
      return () => unsub && unsub()
    }
  }, [conference?.id])

  const handleCloseDeleteModal = () => {
    setDeletedConfigurationId('')
  }

  const handleDeleteConfiguration = async () => {
    setDeletingConfiguration(true)
    await deleteBadgeConfiguration(deletedConfigurationId)
    setDeletingConfiguration(false)
  }

  const handleOpenBadgeBannerModal = () => {
    setOpenBadgeBannerModal(true)
  }

  const handleCloseBadgeBannerModal = () => {
    setOpenBadgeBannerModal(false)
  }

  const handleBadgeBannerUpload = async (name) => {
    try {
      const modifiedConf = { ...conference }
      delete modifiedConf['id']
      modifiedConf['confLogo'] = name
      await dispatch(updateConferenceById(conference?.id, modifiedConf))
      toast.success('Badge banner uploaded.')
    } catch (error) {
      console.error(error)
      toast.error('Could not upload badge banner.')
    }
  }

  const handleBadgeBannerDelete = async () => {
    try {
      const modifiedConf = { ...conference }
      delete modifiedConf['id']
      modifiedConf['confLogo'] = null
      await dispatch(updateConferenceById(conference?.id, modifiedConf))
      toast.success('Badge banner removed.')
    } catch (error) {
      console.error(error)
      toast.error('Could not remove badge banner.')
    }
  }

  const goBack = () => history.goBack()

  const tableHead = [
    'Sr #',
    'Configuration Title',
    'Applied To',
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]
  const options = getTableOptions()

  const formatTableData = (badgeConfigurations) =>
    badgeConfigurations.map((configuration) => {
      return [
        configuration?.srNo,
        configuration?.title,
        configuration?.applicableSubjects?.join(', '),
        <>
          <Tooltip
            title="Edit"
            onClick={() => history.push(`${history.location.pathname}/edit/${configuration?.id}`)}>
            <EditIcon color="action" style={{ cursor: 'pointer' }} />
          </Tooltip>
          <Tooltip title="Delete" onClick={() => setDeletedConfigurationId(configuration?.id)}>
            <DeleteIcon color="action" style={{ marginLeft: 5, cursor: 'pointer' }} />
          </Tooltip>
        </>
      ]
    })

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.pageTitle}>
          Badge Print Configurations
        </Typography>

        <Box sx={{ display: 'flex', gap: '0.75rem' }}>
          <Button
            variant="contained"
            color="primary"
            className={classes.addButton}
            onClick={goBack}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ImageIcon />}
            className={classes.addButton}
            onClick={handleOpenBadgeBannerModal}>
            Badge Banner
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            className={classes.addButton}
            onClick={() => history.push(`${history.location.pathname}/add`)}>
            Add Badge Print Configuration
          </Button>
        </Box>
      </Box>

      {badgeConfigurations?.length ? (
        <Paper variant="outlined" style={{ marginTop: 20 }}>
          <BasicTable
            data={formatTableData(badgeConfigurations)}
            columns={tableHead}
            options={{
              ...options,
              onChangePage: (page) => dispatch(badgesSlice.actions.setCurrentPage(page)),
              onChangeRowsPerPage: (size) => dispatch(badgesSlice.actions.setPageSize(size)),
              page: currentPage,
              rowsPerPage: pageSize
            }}
          />
        </Paper>
      ) : (
        <ResourceNotFound message="No badge print configuration found" />
      )}

      <ConfirmModal
        dialogTitle="Delete Badge Print Configuration"
        dialogText="Do you really want to delete this badge print configuration?"
        open={!!deletedConfigurationId}
        closeModal={handleCloseDeleteModal}
        handleConfirm={handleDeleteConfiguration}
        inProgress={deletingConfiguration}
      />

      <Dialog open={openBadgeBannerModal} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" className={classes.pageTitle}>
              Update Badge Banner
            </Typography>
            <Tooltip title="Close">
              <IconButton className={classes.closeBtn} onClick={handleCloseBadgeBannerModal}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(92,244,255,0.24)', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Recommended Image Dimensions:</strong>
            </Typography>
            <Typography variant="body2">
              • <strong>Landscape Mode:</strong> 1155 × 270 pixels (width × height)
            </Typography>
            <Typography variant="body2">
              • <strong>Portrait Mode:</strong> 855 × 270 pixels (width × height)
            </Typography>
            <Typography variant="body2" style={{ marginTop: 1, marginLeft: 0.5 }}>
              For optimal print quality, use images with 300 DPI resolution. The logo will appear at
              the top of the badge. Using the recommended dimensions will ensure proper display
              without stretching or distortion.
            </Typography>
          </Box>
          <Box sx={{ mt: 2 }}>
            <AvatarPicker
              title="Badge Banner"
              subTitle="Allowed Format: PNG | JPG | JPEG."
              path={Collection.CONFERENCES}
              onUpload={(name) => handleBadgeBannerUpload(name)}
              onDelete={handleBadgeBannerDelete}
              imageName={conference?.confLogo || null}
              conferenceId={conference?.id || null}
              firsTimeUpload={false}
              infoText="This banner will be displayed on badges printed for conference attendees. For Landscape mode: 1155 x 270 pixels. For Portrait mode: 855 x 270 pixels. (300 DPI recommended)"
              imageStyles={{
                height: '100%',
                width: '100%'
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default ConfigurationListing

const useStyles = makeStyles({
  header: {
    marginTop: '10px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  },
  addButton: {
    textTransform: 'none',
    fontWeight: 'bold'
  },
  modalContent: {
    paddingTop: '0px !important'
  },
  pageTitle: {
    fontWeight: 600,
    fontSize: '22px'
  },
  closeBtn: {
    padding: '10px'
  }
})
